import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from 'recharts';
import { demoQuestions, QTYPES, computeResults } from '../../data/demoQuestions';
import { ArrowRight, Award, Clock, Target, TrendingUp, Zap } from 'lucide-react';
import { useDemoTheme } from './DemoThemeContext';

// ── Utilities ─────────────────────────────────────────────────────
function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function useCountUp(target, duration = 1600, delay = 400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      const start = Date.now();
      const frame = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, duration, delay]);
  return value;
}

// ── Custom Tooltip ─────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-xs font-mono"
      style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border-str)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
      {label && <p className="mb-1" style={{ color: 'var(--d-text-3)' }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#a78bfa' }}>{p.name}: {p.value}{p.unit || ''}</p>
      ))}
    </div>
  );
};

// ── Badges ─────────────────────────────────────────────────────────
function computeBadges(answers, timeUsed, results) {
  const badges = [];
  const mcqCorrect = demoQuestions
    .filter((q) => q.type === QTYPES.MCQ)
    .filter((q) => answers[q.id] === q.correct).length;

  if (mcqCorrect === 6) badges.push({ icon: '⬡', label: 'Perfect Theorist', desc: 'All 6 conceptual questions correct', color: '#a78bfa' });
  if (mcqCorrect >= 4) badges.push({ icon: '◈', label: 'JS Fundamentalist', desc: 'Strong grasp of core JavaScript', color: '#818cf8' });

  const codeAnswers = demoQuestions.filter((q) => q.type === QTYPES.CODING);
  const codeAttempted = codeAnswers.filter((q) => {
    const code = answers[q.id]?.code || '';
    return code.replace(q.starterCode || '', '').trim().length > 20;
  }).length;
  if (codeAttempted >= 1) badges.push({ icon: '◇', label: 'Code Architect', desc: 'Attempted both coding challenges', color: '#34d399' });
  if (codeAttempted === 2) badges.push({ icon: '△', label: 'Full-Stack Thinker', desc: 'Completed JS and Python challenges', color: '#10b981' });

  const fileUploaded = !!answers[10]?.fileName;
  if (fileUploaded) badges.push({ icon: '□', label: 'Portfolio Ready', desc: 'Submitted supporting documentation', color: '#f59e0b' });

  const totalTime = 45 * 60 - timeUsed;
  if (totalTime > 20 * 60) badges.push({ icon: '▷', label: 'Speed Demon', desc: 'Completed in under 25 minutes', color: '#ef4444' });

  if (results.percentage >= 80) badges.push({ icon: '✦', label: 'High Performer', desc: 'Scored 80% or above', color: '#fbbf24' });

  // Everyone who completes the assessment gets this badge
  badges.push({ icon: '◎', label: 'Assessment Complete', desc: 'Completed the LambdaQuest demo assessment', color: '#a78bfa' });

  return badges.slice(0, 6);
}

// ── Main Component ─────────────────────────────────────────────────
export default function ResultsDashboard({ answers, timeUsed, onReturnToLanding }) {
  const { isDark } = useDemoTheme();
  const results = computeResults(answers);
  const displayScore = useCountUp(results.score, 1500, 500);
  const displayPct = useCountUp(results.percentage, 1500, 500);

  const mcqQuestions = demoQuestions.filter((q) => q.type === QTYPES.MCQ);
  const mcqCorrect = mcqQuestions.filter((q) => answers[q.id] === q.correct).length;

  // Category breakdown for Donut
  const categoryData = [
    {
      name: 'JS Fundamentals',
      value: mcqQuestions.slice(0, 4).reduce((s, q) => s + (results.questionResults[q.id]?.earned || 0), 0),
      max: 40,
      fill: '#7c3aed',
    },
    {
      name: 'Algorithms',
      value: [5].reduce((s, id) => s + (results.questionResults[id]?.earned || 0), 0) +
             (results.questionResults[9]?.earned || 0),
      max: 20,
      fill: '#3b82f6',
    },
    {
      name: 'Coding',
      value: [7, 8].reduce((s, id) => s + (results.questionResults[id]?.earned || 0), 0),
      max: 20,
      fill: '#22c55e',
    },
    {
      name: 'DOM & Events',
      value: results.questionResults[6]?.earned || 0,
      max: 10,
      fill: '#f59e0b',
    },
    {
      name: 'Portfolio',
      value: results.questionResults[10]?.earned || 0,
      max: 10,
      fill: '#ec4899',
    },
  ];

  // Per-question bar data
  const barData = demoQuestions.map((q) => ({
    name: `Q${q.id}`,
    Earned: results.questionResults[q.id]?.earned || 0,
    Max: q.points,
  }));

  // Radar data
  const radarData = [
    { subject: 'JS Mastery', A: Math.round((mcqCorrect / 6) * 100) },
    { subject: 'Algorithms', A: Math.min(100, Math.round(((results.questionResults[5]?.earned || 0) / 10) * 100)) },
    { subject: 'Code Quality', A: Math.min(100, Math.round((((results.questionResults[7]?.earned || 0) + (results.questionResults[8]?.earned || 0)) / 20) * 100)) },
    { subject: 'Accuracy', A: Math.round((results.score / results.maxScore) * 100) },
    { subject: 'Completeness', A: Math.round((Object.keys(answers).length / demoQuestions.length) * 100) },
  ];

  const badges = computeBadges(answers, timeUsed, results);

  const timeSpent = (45 * 60) - timeUsed;
  const avgPerQuestion = Math.round(timeSpent / demoQuestions.length);

  // AI Insights
  const insights = [
    mcqCorrect >= 4
      ? 'Strong conceptual foundation in JavaScript — closure mechanics and async execution model are well understood.'
      : 'Conceptual areas like closures and the event loop show room for improvement with focused practice.',
    results.questionResults[7]?.earned > 0
      ? 'JavaScript coding submission demonstrates functional understanding of higher-order functions.'
      : 'The JavaScript coding challenge was left incomplete — consider reviewing Array.prototype.reduce patterns.',
    results.questionResults[8]?.earned > 0
      ? 'Python submission shows cross-language algorithmic thinking, a strong differentiator for senior roles.'
      : 'Strengthening Python algorithm patterns (sliding window, two-pointer) would round out your profile.',
    `Assessment completed in ${formatTime(timeSpent)}, suggesting ${timeSpent < 25 * 60 ? 'high confidence' : 'thorough deliberation'} across question types.`,
  ];

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--d-bg)', color: 'var(--d-text-hi)' }}
      data-testid="results-dashboard"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
            >
              LQ
            </div>
            <span
                        className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--d-text-2)', letterSpacing: '0.2em' }}>
              Assessment Complete
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Performance Report
          </h1>
          <p className="text-sm" style={{ color: 'var(--d-text-3)' }}>
            LambdaQuest Technical Assessment · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </motion.div>

        {/* ── Score Hero ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-2xl p-8 mb-6 flex flex-col sm:flex-row items-center gap-8"
          style={{
            background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(79,70,229,0.08) 100%)',
            border: '1px solid rgba(124,58,237,0.2)',
          }}
        >
          <div className="text-center flex-shrink-0">
            <div
              className="text-7xl font-bold font-mono"
              style={{ color: '#a78bfa' }}
              data-testid="score-display"
            >
              {displayScore}
            </div>
            <div className="text-sm mt-1" style={{ color: 'var(--d-text-3)' }}>
              out of {results.maxScore}
            </div>
            <div
              className="text-2xl font-bold mt-2"
              style={{ color: results.percentage >= 70 ? '#4ade80' : results.percentage >= 50 ? '#fbbf24' : '#f87171' }}
            >
              {displayPct}%
            </div>
          </div>

          <div className="w-px self-stretch hidden sm:block" style={{ background: 'var(--d-border)' }} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1 w-full">
            {[
              { label: 'MCQ Score', value: `${mcqCorrect}/6`, icon: Target, color: '#a78bfa' },
              { label: 'Accuracy', value: `${results.percentage}%`, icon: TrendingUp, color: '#4ade80' },
              { label: 'Time Used', value: formatTime(timeSpent), icon: Clock, color: '#f59e0b' },
              { label: 'Answered', value: `${Object.keys(answers).length}/${demoQuestions.length}`, icon: Zap, color: '#60a5fa' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="rounded-xl p-4 text-center" style={{ background: 'var(--d-subtle)', border: '1px solid var(--d-border)' }}>
                <Icon size={16} className="mx-auto mb-2" style={{ color }} />
                <div className="text-lg font-bold font-mono" style={{ color }}>{value}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--d-text-3)' }}>{label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Charts Row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          {/* Donut chart */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl p-6"
            style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
            data-testid="donut-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-4">Category Breakdown</h3>
            <div className="flex justify-center">
              <PieChart width={200} height={180}>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={3} dataKey="value">
                  {categoryData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </div>
            <div className="space-y-2 mt-2">
              {categoryData.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm" style={{ background: cat.fill }} />
                    <span style={{ color: 'var(--d-text-2)' }}>{cat.name}</span>
                  </div>
                  <span className="font-mono" style={{ color: cat.fill }}>
                    {cat.value}/{cat.max}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bar chart */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="rounded-2xl p-6"
            style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
            data-testid="bar-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-4">Per-Question Score</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} margin={{ top: 0, right: 0, left: -28, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--d-grid-stroke)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: 'var(--d-axis-tick)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--d-axis-tick)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--d-hover)' }} />
                <Bar dataKey="Max" fill="var(--d-border)" radius={[3, 3, 0, 0]} name="Max" />
                <Bar dataKey="Earned" fill="#7c3aed" radius={[3, 3, 0, 0]} name="Earned" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Radar chart */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="rounded-2xl p-6"
            style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
            data-testid="radar-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-4">Skill Dimensions</h3>
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--d-grid-stroke)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--d-axis-tick)', fontSize: 10 }} />
                <Radar dataKey="A" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.18} strokeWidth={1.5} name="Score" />
                <Tooltip content={<ChartTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* ── AI Insights + Strengths ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="rounded-2xl p-6"
            style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
            data-testid="ai-insights"
          >
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.3)' }}
              >
                <span className="text-[10px]" style={{ color: '#a78bfa' }}>AI</span>
              </div>
              <h3 className="text-sm font-semibold text-white">AI Insights</h3>
            </div>
            <div className="space-y-3">
              {insights.map((insight, i) => (
                <div key={i} className="flex gap-3 text-xs leading-relaxed" style={{ color: 'var(--d-text-2)' }}>
                  <span className="flex-shrink-0 mt-0.5" style={{ color: '#7c3aed' }}>▸</span>
                  {insight}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            className="rounded-2xl p-6"
            style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
          >
            <h3 className="text-sm font-semibold text-white mb-4">Strengths & Opportunities</h3>
            <div className="mb-4">
              <p className="text-xs font-semibold mb-2" style={{ color: '#4ade80' }}>Strengths</p>
              <div className="flex flex-wrap gap-2">
                {['JavaScript Closures', 'Event Loop', 'Array Methods', 'Big O Analysis'].map((s) => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-lg font-mono"
                    style={{ background: 'rgba(34,197,94,0.1)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: '#f87171' }}>Improvement Areas</p>
              <div className="flex flex-wrap gap-2">
                {['Dynamic Programming', 'Python Optimization', 'System Design'].map((s) => (
                  <span key={s} className="text-xs px-2.5 py-1 rounded-lg font-mono"
                    style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.18)' }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Badges ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="rounded-2xl p-6 mb-5"
          style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
          data-testid="badges-section"
        >
            <div className="flex items-center gap-2 mb-4">
              <Award size={15} style={{ color: '#fbbf24' }} />
              <h3 className="text-sm font-semibold text-white">Achievement Badges</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {badges.map((badge, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.65 + i * 0.07 }}
                  className="rounded-xl p-3 text-center"
                  style={{
                    background: `${badge.color}0f`,
                    border: `1px solid ${badge.color}28`,
                  }}
                  data-testid={`badge-${i}`}
                >
                  <div className="text-xl mb-1.5" style={{ color: badge.color }}>{badge.icon}</div>
                  <div className="text-xs font-semibold mb-1" style={{ color: badge.color }}>{badge.label}</div>
                  <div className="text-xs leading-snug" style={{ color: 'var(--d-text-3)', fontSize: 10 }}>{badge.desc}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

        {/* ── Time Analytics ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="rounded-2xl p-6 mb-8"
          style={{ background: 'var(--d-surface)', border: '1px solid var(--d-border)' }}
          data-testid="time-analytics"
        >
          <div className="flex items-center gap-2 mb-4">
            <Clock size={15} style={{ color: '#60a5fa' }} />
            <h3 className="text-sm font-semibold text-white">Time Analytics</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Time', value: formatTime(timeSpent), color: '#60a5fa' },
              { label: 'Avg / Question', value: formatTime(avgPerQuestion), color: '#a78bfa' },
              { label: 'Time Remaining', value: formatTime(Math.max(0, timeUsed)), color: '#4ade80' },
              { label: 'Pace Rating', value: timeSpent < 20 * 60 ? 'Fast' : timeSpent < 35 * 60 ? 'Optimal' : 'Thorough', color: '#fbbf24' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl px-4 py-3" style={{ background: 'var(--d-subtle)', border: '1px solid var(--d-border-sub)' }}>
                <div className="text-base font-bold font-mono" style={{ color }}>{value}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--d-text-3)' }}>{label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── CTA Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="rounded-2xl p-8 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(79,70,229,0.05) 50%, rgba(99,102,241,0.08) 100%)',
            border: '1px solid rgba(124,58,237,0.2)',
          }}
          data-testid="results-cta"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Experience LambdaQuest at Enterprise Scale
          </h2>
          <p className="text-sm mb-7 max-w-lg mx-auto" style={{ color: 'var(--d-text-2)' }}>
            This was a demo. The full platform includes adaptive question banks, real-time proctoring,
            detailed analytics, and custom workflows for your organization.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://cal.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="schedule-demo-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                color: '#fff',
                boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
              }}
            >
              Schedule a Demo
              <ArrowRight size={15} />
            </a>

            <button
              data-testid="join-waitlist-results-btn"
              onClick={() => {
                if (onReturnToLanding) {
                  onReturnToLanding();
                  setTimeout(() => {
                    const el = document.getElementById('waitlist');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 300);
                }
              }}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: 'var(--d-hover)',
                border: '1px solid var(--d-border-str)',
                color: 'var(--d-text-1)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--d-text-1)')}
            >
              Join Waitlist
            </button>

            <button
              data-testid="return-landing-btn"
              onClick={onReturnToLanding}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm transition-colors"
              style={{ color: 'var(--d-text-3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--d-text-1)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--d-text-3)')}
            >
              Return to Site
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
